import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApolloClient, useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { useParams } from "react-router-dom";
import { useStore } from "react-redux";
import { RootState, useAppDispatch, useAppSelector } from "@services/redux";
import { clearDraftOrder, orderReducer, updateDraftOrder } from "../redux/order/order.reducer";
import { toggleSnackbar, userDeatils } from "../../ReactiveVariables/index";
import { getUserId } from "../../utils/user-format.utils";
import {
  FINISH_GROUP_ORDER,
  GROUP_ORDER,
  GROUP_ORDER_BY_TOKEN,
  REOPEN_GROUP_ORDER,
  REOPEN_GROUP_ORDER_FOR_OFFICE,
  SAVE_GROUP_CART,
  SAVE_GROUP_CART_BY_TOKEN,
  START_GROUP_CHECKOUT,
} from "../../Graphql/group-order.gql";
import {
  browserStorage,
  buildDraftFromGroupOrder,
  buildSaveInput,
  groupSaveSnapshot,
  isOfficeReadOnly,
  mergeSaveResult,
  serverBudget,
  stashPersonalDraft,
} from "./group-order.utils";

export type GroupSaveStatus = "idle" | "saving" | "saved" | "error";
export type GroupFinishResult = { ok: true } | { ok: false; missing?: string[] };

export interface GroupOrderContextValue {
  mode: "office" | "rep";
  groupOrder: any | null; // latest server GroupOrder
  listPath: string; // this group's own caterer list
  loadFailed: boolean; // the query failed for a reason other than not-found
  linkInvalid: boolean; // a save was refused with not-found (e.g. the link was replaced)
  loading: boolean;
  hydrated: boolean; // draft holds this group order's cart
  readOnly: boolean; // office: isOfficeReadOnly; rep: Placed/Cancelled
  budget: {
    subtotal: number;
    estimatedTotal: number | null; // fees, tax and tip included; null when not priced
    totalBudget: number;
    perPersonTotal: number;
    overBudgetBy: number;
    overBudgetReason: string | null;
    isOver: boolean;
    headCount: number; // the headcount the per-person limit uses
  };
  saving: boolean; // deprecated: use saveStatus (kept for one release)
  saveStatus: GroupSaveStatus;
  lastSavedAt: Date | null;
  conflictNotice: string | null; // stays until dismissConflict()
  dismissConflict(): void;
  finish(): Promise<GroupFinishResult>; // office only: flush, then mark Ready
  makeChanges(): Promise<void>; // office only: reopen a Ready order for editing
  startCheckout(): Promise<void>; // rep only: flush + START_GROUP_CHECKOUT; rejects (after telling the user) if checkout can't go ahead
  refresh(): Promise<void>; // refetch the group order without re-hydrating the draft
}

const GroupOrderContext = createContext<GroupOrderContextValue | null>(null);
export const useGroupOrder = () => useContext(GroupOrderContext);

const SAVE_DELAY_MS = 800;

// Fields buildDraftFromGroupOrder leaves off the draft when the server hasn't set them.
const SERVER_OPTIONAL_FIELDS = ["driverTip", "driverTipType", "tipPercentage", "tablewareDeclined"];
// What a freshly cleared draft holds for those fields (the order reducer's initial draft).
const INITIAL_DRAFT: any = orderReducer(undefined, { type: "groupOrder/initialDraft" }).draft;

// The draft as the server has it right after a hydrate: the store's draft, with the
// server-optional fields set to the server's value or, when unset, the cleared-draft
// default. A local value that differs (e.g. a tip set but not yet saved) still counts
// as unsaved, while the cleared-draft defaults never trigger a save on their own.
const serverSavedDraft = (storeDraft: any, go: any) => {
  const saved = { ...storeDraft };
  SERVER_OPTIONAL_FIELDS.forEach((key) => {
    saved[key] = go[key] !== null && go[key] !== undefined ? go[key] : INITIAL_DRAFT[key];
  });
  return saved;
};
const RETRY_DELAY_MS = 5000;
// Network failures in a row before autosave stops retrying on its own. The next
// draft change starts a fresh round.
const MAX_NETWORK_FAILURES = 3;
// Shortest gap between two refreshes when the user comes back to the tab.
const RETURN_REFRESH_MIN_MS = 5000;

export const GroupOrderProvider = ({
  mode,
  children,
}: {
  mode: "office" | "rep";
  children: React.ReactNode;
}) => {
  const { token, groupOrderId } = useParams();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const draft = useAppSelector((state) => state.order.draft);
  const user = useReactiveVar(userDeatils);
  const repCustomerId = getUserId(user) || "";

  const { data, loading, error, refetch } = useQuery(
    mode === "office" ? GROUP_ORDER_BY_TOKEN : GROUP_ORDER,
    {
      variables: mode === "office" ? { token } : { id: groupOrderId },
      fetchPolicy: "network-only",
    },
  );

  // The cache is not normalized (addTypename: false), so mutation results never
  // update the query result. Keep the latest server copy in state instead.
  const [groupOrder, setGroupOrderState] = useState<any | null>(null);
  // Counts every write to groupOrder, so a background refresh that started earlier
  // can tell a newer result (a save, reopen or finish) has landed and back off.
  const writeGenRef = useRef(0);
  const setGroupOrder = useCallback((value: React.SetStateAction<any | null>) => {
    writeGenRef.current += 1;
    setGroupOrderState(value);
  }, []);
  const client = useApolloClient();
  useEffect(() => {
    const fromQuery = data?.groupOrderByToken || data?.groupOrder;
    if (fromQuery) setGroupOrder(fromQuery);
  }, [data]);

  const [saveByToken] = useMutation(SAVE_GROUP_CART_BY_TOKEN);
  const [saveByOwner] = useMutation(SAVE_GROUP_CART);
  const [startCheckoutMutation] = useMutation(START_GROUP_CHECKOUT);
  const [reopenMutation] = useMutation(REOPEN_GROUP_ORDER);
  const [finishMutation] = useMutation(FINISH_GROUP_ORDER);
  const [reopenForOfficeMutation] = useMutation(REOPEN_GROUP_ORDER_FOR_OFFICE);

  const versionRef = useRef<number | null>(null);
  const lastSavedRef = useRef<string>("");
  const hydratedIdRef = useRef<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<GroupSaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [conflictNotice, setConflictNotice] = useState<string | null>(null);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const reopenStartedRef = useRef<string | null>(null);
  const [reopenSettledId, setReopenSettledId] = useState<string | null>(null);

  // Read through a ref so hydrate (and everything built on it) keeps its identity across draft edits.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const hydrate = useCallback(
    (go: any) => {
      const next = buildDraftFromGroupOrder(go, { mode, repCustomerId });
      const current = draftRef.current;
      if (current.groupOrderId === go._id) {
        // Already this group's draft (e.g. back from Review Order): take the server's cart,
        // caterer and delivery fields and keep checkout-only ones the server hasn't set.
        dispatch(updateDraftOrder(next));
      } else {
        // Stash the personal cart before it is replaced (no-op if it already is a group draft).
        stashPersonalDraft(browserStorage(), current);
        dispatch(clearDraftOrder());
        dispatch(updateDraftOrder(next));
      }
      versionRef.current = go.version;
      // Not `next`: unset tip/tableware fields would differ from the draft's cleared
      // defaults and every load would save them. Not the plain store draft either: in
      // the update branch it keeps local tip/tableware the server doesn't have, which
      // would then never be saved. See serverSavedDraft.
      lastSavedRef.current = groupSaveSnapshot(serverSavedDraft(store.getState().order.draft, go));
      hydratedIdRef.current = go._id;
    },
    [dispatch, mode, repCustomerId, store],
  );

  // Initial load: the server cart replaces the draft once per group order.
  // Office: hydrates whatever the status, so a Ready order can go back to editing via makeChanges.
  useEffect(() => {
    if (!groupOrder || hydratedIdRef.current === groupOrder._id) return;
    if (mode === "rep") {
      // Placed or cancelled is read-only for the rep: keep their own cart as it is.
      if (["Placed", "Cancelled"].includes(groupOrder.status)) return;
      // Reopen before hydrating. If the order was already paid, the server returns it
      // Placed and the shell shows it read-only instead of an editable cart.
      if (groupOrder.status === "CheckingOut" && reopenSettledId !== groupOrder._id) {
        if (reopenStartedRef.current === groupOrder._id) return; // already in flight
        const id = groupOrder._id;
        reopenStartedRef.current = id;
        reopenMutation({ variables: { id } })
          .then((res) => {
            const reopened = res.data?.reopenGroupOrder;
            if (reopened) setGroupOrder(reopened);
          })
          .catch(() => undefined)
          .finally(() => setReopenSettledId(id));
        return;
      }
    }
    hydrate(groupOrder);
  }, [groupOrder, hydrate, mode, reopenMutation, reopenSettledId]);

  const readOnly =
    !groupOrder || linkInvalid
      ? true
      : mode === "office"
        ? isOfficeReadOnly(groupOrder, new Date())
        : ["Placed", "Cancelled"].includes(groupOrder.status);

  // Latest values for the serialized save chain (avoids stale closures).
  const groupOrderRef = useRef(groupOrder);
  const readOnlyRef = useRef(readOnly);
  const hydrateRef = useRef(hydrate);
  const mountedRef = useRef(true);
  const chainRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const failureCountRef = useRef(0);
  const networkFailuresRef = useRef(0); // consecutive; reset on success and on a draft change
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true));
  groupOrderRef.current = groupOrder;
  readOnlyRef.current = readOnly;
  hydrateRef.current = hydrate;

  // Refetch and store the latest server copy; the draft is not re-hydrated.
  const refresh = useCallback(async () => {
    const fresh = (await refetch()).data;
    const latest = fresh?.groupOrderByToken || fresh?.groupOrder;
    if (latest && mountedRef.current) setGroupOrder(latest);
  }, [refetch]);

  // Refetch and replace the draft with the server's copy.
  const refreshAndHydrate = useCallback(async () => {
    const fresh = (await refetch()).data;
    const latest = fresh?.groupOrderByToken || fresh?.groupOrder;
    if (latest && mountedRef.current) {
      setGroupOrder(latest);
      hydrateRef.current(latest);
    }
  }, [refetch]);

  // Resolves true when there was nothing to save or the save succeeded, false on failure. Never rejects.
  const runSave = useCallback(async (draftOverride?: any): Promise<boolean> => {
    const go = groupOrderRef.current;
    // The store, not the render-time ref: a dispatch right before flush()/finish() is included.
    // On unmount the caller passes the draft it captured, since leaving group mode clears the store.
    const current = draftOverride || store.getState().order.draft;
    if (!go || readOnlyRef.current || current.groupOrderId !== go._id) return true;
    const snapshot = groupSaveSnapshot(current);
    if (snapshot === lastSavedRef.current) return true;
    const cart = buildSaveInput(current, versionRef.current);
    if (mountedRef.current) {
      setSaving(true);
      setSaveStatus("saving");
    }
    try {
      const result =
        mode === "office"
          ? (await saveByToken({ variables: { token, cart } })).data.saveGroupCartByToken
          : (await saveByOwner({ variables: { id: go._id, cart } })).data.saveGroupCart;
      versionRef.current = result.version;
      lastSavedRef.current = snapshot;
      networkFailuresRef.current = 0;
      if (mountedRef.current) {
        setSaveStatus("saved");
        setLastSavedAt(new Date());
        setGroupOrder((prev: any) => mergeSaveResult(prev, result));
      }
      return true;
    } catch (err: any) {
      failureCountRef.current += 1;
      if (!mountedRef.current) return false; // unmounted: never touch the draft
      const extensions = err?.graphQLErrors?.[0]?.extensions;
      const code = extensions?.code;
      if (code === "GROUP_ORDER_NOT_FOUND") {
        // The link was replaced (or the group order is gone): the shell shows the invalid-link message.
        setSaveStatus("error");
        setLinkInvalid(true);
        return false;
      }
      if (!code || code === "INTERNAL_SERVER_ERROR") {
        // Network or server hiccup: keep the local edit and retry shortly, a few times at most.
        setSaveStatus("error");
        networkFailuresRef.current += 1;
        if (networkFailuresRef.current < MAX_NETWORK_FAILURES) {
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            saveRef.current();
          }, RETRY_DELAY_MS);
        }
        return false;
      }
      if (code === "BAD_USER_INPUT") {
        setSaveStatus("error");
        toggleSnackbar({ status: true, message: err.message, variant: "error" });
        return false;
      }
      if (code === "GROUP_ORDER_CLOSED" || code === "GROUP_ORDER_VERSION_CONFLICT") {
        const status = extensions?.status;
        setSaveStatus("idle");
        setConflictNotice(
          code === "GROUP_ORDER_CLOSED" && status === "CheckingOut"
            ? `${go.repFirstName || "Your rep"} is checking out, so this cart can't be changed right now.`
            : code === "GROUP_ORDER_CLOSED"
              ? "This group order can't be changed right now."
              : "Someone else just changed this order. We've loaded the latest version; please check your last change.",
        );
      } else {
        // Anything else (e.g. an invalid cart): not a conflict, so no conflict notice.
        setSaveStatus("error");
        toggleSnackbar({
          status: true,
          message: "Couldn't save the cart. Please try again.",
          variant: "error",
        });
      }
      try {
        await refreshAndHydrate();
      } catch (refetchErr) {
        // keep the current draft; the next change retries the save
      }
      return false;
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [mode, refreshAndHydrate, saveByOwner, saveByToken, store, token]);

  // Saves are serialized: each runs after the previous one and reads the newest draft/version.
  const save = useCallback(
    (draftOverride?: any): Promise<boolean> => {
      const next = chainRef.current.then(() => runSave(draftOverride));
      chainRef.current = next;
      return next;
    },
    [runSave],
  );
  saveRef.current = save;

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  // `state` and `placeId` are set by buildDraftFromGroupOrder but not typed on IOrderPayload.
  const draftState = (draft as any).state;
  const draftPlaceId = (draft as any).placeId;

  // Debounced autosave whenever a saved field changes. A change also replaces any
  // pending retry and starts a fresh round of network retries.
  useEffect(() => {
    clearTimer();
    networkFailuresRef.current = 0;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      save();
    }, SAVE_DELAY_MS);
    return clearTimer;
  }, [
    draft.cartDetails,
    draft.catererId,
    draft.street,
    draft.zip,
    draft.lat,
    draft.lng,
    draft.suit,
    draft.city,
    draftState,
    draft.locationName,
    draftPlaceId,
    draft.deliveryDate,
    draft.headCount,
    draft.contactPerson,
    draft.contactNo,
    draft.instruction,
    draft.driverTip,
    draft.driverTipType,
    draft.tipPercentage,
    draft.tablewareDeclined,
    clearTimer,
    save,
  ]);

  // Coming back to the tab: pick up changes made elsewhere, such as the rep editing the
  // budget. Skipped while an edit is waiting to save, while a refresh is running, and
  // within a few seconds of the last one (focus and visibilitychange both fire). The
  // fetch bypasses the cache so the query effect above never applies it, and the
  // result is dropped if anything wrote groupOrder meanwhile.
  const returnRefreshRef = useRef({ inFlight: false, at: 0 });
  useEffect(() => {
    const onReturn = () => {
      const r = returnRefreshRef.current;
      if (document.visibilityState === "hidden" || timerRef.current || r.inFlight) return;
      if (Date.now() - r.at < RETURN_REFRESH_MIN_MS) return;
      r.inFlight = true;
      r.at = Date.now();
      const gen = writeGenRef.current;
      client
        .query({
          query: mode === "office" ? GROUP_ORDER_BY_TOKEN : GROUP_ORDER,
          variables: mode === "office" ? { token } : { id: groupOrderId },
          fetchPolicy: "no-cache",
        })
        .then((res) => {
          const latest = res.data?.groupOrderByToken || res.data?.groupOrder;
          if (latest && mountedRef.current && gen === writeGenRef.current) {
            setGroupOrderState(latest);
          }
        })
        .catch(() => undefined)
        .finally(() => {
          r.inFlight = false;
        });
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [client, groupOrderId, mode, token]);

  // Closing or reloading the tab: send a pending edit (or pending retry) right away.
  useEffect(() => {
    const onUnload = () => {
      if (timerRef.current) {
        clearTimer();
        save();
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [clearTimer, save]);

  // On unmount, flush a pending edit instead of dropping it (fire and forget). The draft is
  // read now: App's draft guard clears it in the same commit, before a queued save would run.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
      save(store.getState().order.draft);
    };
  }, [clearTimer, save, store]);

  const flush = useCallback(async (): Promise<boolean> => {
    clearTimer();
    const failuresBefore = failureCountRef.current;
    const ok = await save();
    return ok && failureCountRef.current === failuresBefore;
  }, [clearTimer, save]);

  // Rejects if checkout can't go ahead, so the caller does not move on. The user has
  // already been told why (save failure, start failure, or a newer cart to review).
  const startCheckout = useCallback(async () => {
    clearTimer();
    // Any save settling after the click (including one already in flight) that fails must stop checkout.
    if (!(await flush())) throw new Error("Group cart could not be saved");
    const versionBefore = versionRef.current;
    let started: any;
    try {
      const res = await startCheckoutMutation({ variables: { id: groupOrderRef.current._id } });
      started = res.data?.startGroupCheckout;
    } catch (err) {
      if (mountedRef.current) {
        toggleSnackbar({
          status: true,
          message: "We couldn't start checkout. Please try again.",
          variant: "error",
        });
      }
      throw err;
    }
    if (!started) return;
    setGroupOrder(started);
    if (started.version !== versionBefore) {
      // The office changed the cart after this page loaded: show it before the rep pays for it.
      if (mountedRef.current) {
        hydrateRef.current(started);
        toggleSnackbar({
          status: true,
          message: "This cart was just updated. Showing the latest version.",
          variant: "info",
        });
      }
      throw new Error("Group cart was updated");
    }
    versionRef.current = started.version;
  }, [clearTimer, flush, startCheckoutMutation]);

  // Office: save the latest edit, then mark Ready. The server checks the version,
  // so a cart whose last save hasn't landed can't be finished.
  const finish = useCallback(async (): Promise<GroupFinishResult> => {
    if (!(await flush())) return { ok: false };
    try {
      const res = await finishMutation({ variables: { token, version: versionRef.current } });
      const updated = res.data?.finishGroupOrder;
      if (updated) {
        if (typeof updated.version === "number") versionRef.current = updated.version;
        if (mountedRef.current) setGroupOrder((prev: any) => mergeSaveResult(prev, updated));
      }
      return { ok: true };
    } catch (err: any) {
      const ext = err?.graphQLErrors?.[0]?.extensions;
      if (ext?.code === "GROUP_ORDER_NOT_READY") return { ok: false, missing: ext.missing || [] };
      toggleSnackbar({
        status: true,
        message:
          ext?.code === "GROUP_ORDER_VERSION_CONFLICT"
            ? "This order was just changed. Please check it and send again."
            : ext?.code === "GROUP_ORDER_TOO_SOON"
              ? "You just sent this. Please wait a minute before sending again."
              : "We couldn't send this to your rep. Please try again.",
        variant: "error",
      });
      if (ext?.code === "GROUP_ORDER_VERSION_CONFLICT") {
        await refreshAndHydrate().catch(() => undefined);
      }
      return { ok: false };
    }
  }, [flush, finishMutation, refreshAndHydrate, token]);

  // Office: take a Ready order back to Open and reload the editable cart.
  const makeChanges = useCallback(async () => {
    try {
      const res = await reopenForOfficeMutation({ variables: { token } });
      const updated = res.data?.reopenGroupOrderForOffice;
      if (updated && mountedRef.current) {
        setGroupOrder((prev: any) => mergeSaveResult(prev, updated));
        hydrateRef.current(updated);
      }
    } catch (e) {
      toggleSnackbar({
        status: true,
        message: "That didn't go through. Please try again.",
        variant: "error",
      });
      await refresh().catch(() => undefined);
    }
  }, [refresh, reopenForOfficeMutation, token]);

  const dismissConflict = useCallback(() => setConflictNotice(null), []);

  // The API's figures: it prices the cart with every fee, tax and tip on each save, so an
  // edit shows up here once its save returns. The live headcount is returned too, so the
  // per-person hint matches the draft.
  const budget = useMemo(() => {
    const live = !!groupOrder && draft.groupOrderId === groupOrder._id;
    const headCount =
      Number((live && draft.headCount) || groupOrder?.headCount || groupOrder?.maxHeadCount) || 0;
    return { ...serverBudget(groupOrder), headCount };
  }, [draft.groupOrderId, draft.headCount, groupOrder]);

  // Loading until a fetched group order has been copied into state, so no frame shows wrong copy.
  const fetched = data?.groupOrderByToken || data?.groupOrder;
  const notFound = !!error?.graphQLErrors?.some(
    (e: any) => e.extensions?.code === "GROUP_ORDER_NOT_FOUND",
  );
  const hydrated = !!groupOrder && draft.groupOrderId === groupOrder._id;
  const value = useMemo<GroupOrderContextValue>(
    () => ({
      mode,
      groupOrder,
      listPath: mode === "office" ? `/group/${token}` : `/group-orders/${groupOrderId}/caterers`,
      loadFailed: !!error && !notFound,
      linkInvalid,
      loading: loading || (!!fetched && !groupOrder),
      hydrated,
      readOnly,
      budget,
      saving,
      saveStatus,
      lastSavedAt,
      conflictNotice,
      dismissConflict,
      finish,
      makeChanges,
      startCheckout,
      refresh,
    }),
    [
      mode,
      groupOrder,
      token,
      groupOrderId,
      error,
      notFound,
      linkInvalid,
      loading,
      fetched,
      hydrated,
      readOnly,
      budget,
      saving,
      saveStatus,
      lastSavedAt,
      conflictNotice,
      dismissConflict,
      finish,
      makeChanges,
      startCheckout,
      refresh,
    ],
  );
  return <GroupOrderContext.Provider value={value}>{children}</GroupOrderContext.Provider>;
};
